/**
 * Qualification avancée de la fiche : le verdict du commercial, motivé.
 *
 * À côté du statut, pas à sa place : « Hors cible — hors périmètre du
 * secteur », « À vérifier — non référencée sur le web, revoir dans 3 mois »,
 * « Cible — secteur réel : toilettage » (plus précis que le NAF du registre).
 * La revérification pose une tâche et remonte dans la liste quand elle est
 * échue (filtre « À revérifier »).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Pencil, X, Loader2, CalendarClock } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { VERDICTS, MOTIFS, VERDICT_STYLE, verdictLabel, motifLabel, recheckDue, inDays } from "@/crm/qualification";
import { cn } from "@/lib/utils";

const FIELD = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500";
const LIBRE = "__libre__";

export default function QualificationPanel({ company, onSaved }) {
  const q = company?.qualification || null;
  const [editing, setEditing] = useState(false);
  const [options, setOptions] = useState({ verdicts: VERDICTS, motifs: MOTIFS, secteurs: [] });
  useEffect(() => {
    api.get("/companies/qualification-options").then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  if (!editing) {
    return (
      <div data-testid="qualification-panel"
        className={cn("mb-4 flex flex-wrap items-start gap-3 rounded-xl border p-4",
          q ? VERDICT_STYLE[q.verdict] : "border-slate-200 bg-white")}>
        <ClipboardCheck size={20} className="mt-0.5 shrink-0 opacity-80" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-sm font-bold">Qualification : {q ? verdictLabel(q.verdict) : "non qualifiée"}</span>
            {(q?.motifs || []).map((m) => (
              <span key={m} className="rounded-md bg-white/70 px-1.5 py-0.5 text-[11.5px] font-medium">{motifLabel(m)}</span>
            ))}
            {q && recheckDue(q) && (
              <span data-testid="qualification-due" className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-1.5 py-0.5 text-[11.5px] font-semibold text-white">
                <CalendarClock size={12} /> À revérifier
              </span>
            )}
          </div>
          {q ? (
            <div className="mt-1 text-[13px] opacity-90">
              {q.secteur_reel_label && <>Secteur réel : <b>{q.secteur_reel_label}</b>{q.activite_reelle && <> — {q.activite_reelle}</>} · </>}
              {!q.secteur_reel_label && q.activite_reelle && <>Activité réelle : <b>{q.activite_reelle}</b> · </>}
              {q.recheck_at && <>Revérifier le {new Date(q.recheck_at).toLocaleDateString("fr-FR")} · </>}
              {q.qualified_by_name && <>par {q.qualified_by_name}</>}
              {q.qualified_at && <> le {new Date(q.qualified_at).toLocaleDateString("fr-FR")}</>}
              {q.note && <div className="mt-1 whitespace-pre-wrap">{q.note}</div>}
            </div>
          ) : (
            <div className="mt-1 text-[13px] text-slate-500">
              Cible, hors cible ou à vérifier — avec le motif, le secteur réel constaté et une date de revérification.
            </div>
          )}
        </div>
        <button data-testid="qualification-edit" onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-current/20 bg-white/70 px-3 py-1.5 text-[13px] font-medium hover:bg-white">
          <Pencil size={14} /> {q ? "Modifier" : "Qualifier"}
        </button>
      </div>
    );
  }
  return <QualificationForm company={company} options={options} onClose={() => setEditing(false)}
    onSaved={() => { setEditing(false); onSaved?.(); }} />;
}

function QualificationForm({ company, options, onClose, onSaved }) {
  const q = company?.qualification || {};
  const secteurs = options.secteurs || [];
  const connu = secteurs.some((s) => s.slug === q.secteur_reel);
  const [verdict, setVerdict] = useState(q.verdict || "");
  const [motifs, setMotifs] = useState(q.motifs || []);
  const [secteur, setSecteur] = useState(connu ? q.secteur_reel : (q.secteur_reel ? LIBRE : ""));
  const [secteurLibre, setSecteurLibre] = useState(connu ? "" : (q.secteur_reel || ""));
  const [activite, setActivite] = useState(q.activite_reelle || "");
  const [note, setNote] = useState(q.note || "");
  const [recheck, setRecheck] = useState(q.recheck_at || "");
  const [saving, setSaving] = useState(false);

  const toggleMotif = (m) => setMotifs((s) => s.includes(m) ? s.filter((x) => x !== m) : [...s, m]);
  const payload = useMemo(() => ({
    verdict, motifs,
    secteur_reel: secteur === LIBRE ? secteurLibre.trim() : secteur,
    activite_reelle: activite.trim(), note: note.trim(), recheck_at: recheck || null,
  }), [verdict, motifs, secteur, secteurLibre, activite, note, recheck]);

  const save = async (clear = false) => {
    setSaving(true);
    try {
      await api.patch(`/companies/${company.id}/qualification`, clear ? { verdict: "" } : payload);
      toast.success(clear ? "Qualification effacée" : "Qualification enregistrée");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="qualification-form" className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-sm font-bold text-slate-900">Qualifier la fiche</h3>
        <button onClick={onClose} aria-label="Fermer" className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {(options.verdicts || VERDICTS).map((v) => (
          <button key={v.value} type="button" data-testid={`verdict-${v.value}`} onClick={() => setVerdict(v.value)}
            className={cn("rounded-lg border px-3 py-1.5 text-[13px] font-medium",
              verdict === v.value ? VERDICT_STYLE[v.value] + " ring-2 ring-offset-1 ring-indigo-300" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="mb-3">
        <div className="mb-1 text-xs font-semibold text-slate-600">Motifs</div>
        <div className="flex flex-wrap gap-1.5">
          {(options.motifs || MOTIFS).map((m) => (
            <button key={m.value} type="button" data-testid={`motif-${m.value}`} onClick={() => toggleMotif(m.value)}
              aria-pressed={motifs.includes(m.value)}
              className={cn("rounded-lg border px-2.5 py-1 text-[12.5px]",
                motifs.includes(m.value) ? "border-indigo-400 bg-indigo-100 text-indigo-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Secteur réel constaté</span>
          <select data-testid="secteur-reel" value={secteur} onChange={(e) => setSecteur(e.target.value)} className={cn(FIELD, "mt-1 bg-white")}>
            <option value="">— Comme au registre ({company.libelle_naf || company.naf || "inconnu"}) —</option>
            {secteurs.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
            <option value={LIBRE}>Autre (préciser)</option>
          </select>
          {secteur === LIBRE && (
            <input data-testid="secteur-reel-libre" value={secteurLibre} onChange={(e) => setSecteurLibre(e.target.value)}
              placeholder="Ex. Toilettage canin" className={cn(FIELD, "mt-1.5")} />
          )}
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Activité réelle (précision)</span>
          <input data-testid="activite-reelle" value={activite} onChange={(e) => setActivite(e.target.value)}
            placeholder="Ex. pizzeria à emporter, pas de salle" className={cn(FIELD, "mt-1")} />
        </label>
      </div>

      <div className="mb-3">
        <span className="text-xs font-semibold text-slate-600">Revérifier plus tard</span>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <input type="date" data-testid="recheck-at" value={recheck} onChange={(e) => setRecheck(e.target.value)} className={cn(FIELD, "w-auto")} />
          {[["1 mois", 30], ["3 mois", 90], ["6 mois", 180]].map(([l, n]) => (
            <button key={n} type="button" data-testid={`recheck-${n}`} onClick={() => setRecheck(inDays(n))}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12.5px] text-slate-600 hover:bg-slate-50">{l}</button>
          ))}
          {recheck && <button type="button" onClick={() => setRecheck("")} className="text-[12.5px] text-slate-500 underline">Sans date</button>}
        </div>
        <p className="mt-1 text-[11.5px] text-slate-400">Une tâche « Revérifier la fiche » est posée à cette date, et la fiche remonte dans le filtre « À revérifier ».</p>
      </div>

      <textarea data-testid="qualification-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Note : ce qui a été constaté, où, comment…" className={FIELD} />

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {q.verdict && (
          <button type="button" data-testid="qualification-clear" disabled={saving} onClick={() => save(true)}
            className="mr-auto rounded-lg px-3 py-2 text-sm text-rose-600 hover:bg-rose-50">Effacer la qualification</button>
        )}
        <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">Annuler</button>
        <button type="button" data-testid="qualification-save" disabled={saving || !verdict} onClick={() => save(false)}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {saving && <Loader2 size={14} className="animate-spin" />} Enregistrer
        </button>
      </div>
    </div>
  );
}
